import { Canceler } from 'axios';
import { observer } from 'mobx-react';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import ModalPopup from '../../../ModalPopup/ModalPopup';
import PhoneInput from '../../PhoneInput/PhoneInput';
import Spinner from '../../Spinner/Spinner';
import DropDownOption from '../components/DropDownOption/DropDownOption';
import { DEBOUNCE_TIME, defaultPagination } from 'src/constants/main';
import { countriesStore } from 'src/mobx/countriesStore';
import client from 'src/services/client';
import { IPagination } from 'src/types/main';
import { ICustomer } from 'src/types/user';
import { getCancelToken } from 'src/utils/helpers';
import { debounce } from 'src/utils/perfomance';
import { isValidCountryCode } from 'src/utils/validation';
import theme from '~/constants/styles';
import logger from '~/services/logger';
import './style.scss';

export type ICustomerPickerOption = {
  customer: ICustomer;
};

export interface ICustomerPickerProps {
  onSelect: (option: ICustomerPickerOption | undefined) => void;
  onChange?: (text: string) => void;
  phone: string;
  error?: boolean;
  disabled?: boolean;
}

interface IState {
  loading: boolean;
  phone: string;
  pagination: IPagination<ICustomer>;
  customer?: ICustomer;
  visible: boolean;
}

@observer
class CustomerPicker extends React.Component<ICustomerPickerProps, IState> {
  cancel: Canceler | null = null;
  modal: ModalPopup | null = null;

  state: IState = {
    loading: false,
    phone: this.props.phone,
    pagination: defaultPagination,
    customer: undefined,
    visible: false,
  };

  changeListVisibility = (visible: boolean) => {
    if (visible) {
      this.modal?.openModalPopup();
    } else {
      this.modal?.closeModalPopup();
    }
  };

  findCustomer = async () => {
    try {
      const phone = this.state.phone;
      if (!phone || phone.length < 3) {
        this.setState({ pagination: defaultPagination });
        return;
      }
      this.setState(() => ({ loading: true }));
      if (this.cancel) this.cancel('Cancelled');
      const customers: IPagination<ICustomer> = await client.get(`/v3/users`, {
        params: { phone, is_delete: 0 },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      this.setState({ loading: false, pagination: customers });
      if (customers.data.length > 1 && this.modal?.state.rect === null) this.changeListVisibility(true);
    } catch (error: any) {
      this.setState(() => ({ loading: false }));
      logger.error({ error, place: 'CustomerPicker findCustomer' });
      return [];
    }
  };

  handleFindCustomer = () => {
    this.findCustomer()
      .then(() => {
        const customer = this.state.pagination.data.find((c) => c.mobile === this.state.phone);
        if (customer) {
          this.setState({ customer });
          this.props.onSelect({ customer });
        } else {
          this.props.onSelect(undefined);
        }
        return;
      })
      .catch((e) => {
        logger.log(e);
      });
    if (this.props.onChange) this.props.onChange(this.state.phone || '');
  };

  handleChangePhone = debounce(this.handleFindCustomer, DEBOUNCE_TIME.LARGE);

  onInputChange = (text: string) => {
    this.setState({ phone: text, customer: undefined }, this.handleChangePhone);
  };

  onCustomerSelect = (customer: ICustomer) => {
    this.setState({ customer });
    this.props.onSelect({ customer });
    this.modal?.closeModalPopup();
  };

  renderCustomer = (item: ICustomer) => {
    return (
      <DropDownOption value={item} onClick={this.onCustomerSelect}>
        <span className="text-size-16 family-semibold">
          {item.name} {item.mobile}
        </span>
      </DropDownOption>
    );
  };

  get disabled(): boolean {
    return this.props.disabled || countriesStore.loading;
  }

  public render() {
    const countryCodeValidStatus = isValidCountryCode(this.props.phone, countriesStore.countries);

    return (
      <div className="d-flex flex-column">
        <div className="d-flex customer-picker">
          <ModalPopup
            autoClose
            ref={(modal) => (this.modal = modal)}
            content={
              <div className="d-flex flex-column hide-scrollbar customer-picker-list animate__faster animate__animated animate__fadeInLeft">
                <div className="d-flex flex-column customer-picker-content">
                  {this.state.pagination.data.map((item) => this.renderCustomer(item))}
                </div>
              </div>
            }
          >
            <div className="d-flex">
              {countriesStore.loading ? (
                <Skeleton className="d-flex flex-column" style={{ borderRadius: 12 }} width={269} height={46} />
              ) : (
                <PhoneInput
                  country={countryCodeValidStatus ? countriesStore.defaultCountryCode : undefined}
                  onlyCountries={countriesStore.countriesForPhoneInput}
                  countryCodeEditable={false}
                  disabled={this.disabled}
                  disableDropdown={this.disabled}
                  inputProps={{ disabled: this.disabled }}
                  error={this.props.error}
                  className="customer-picker-input bg-white"
                  onFocus={() => this.changeListVisibility(true)}
                  onBlur={() => this.changeListVisibility(false)}
                  value={this.props.phone}
                  onChange={this.onInputChange}
                />
              )}
              {this.state.loading ? <Spinner color={theme.colors.gray2} /> : null}
            </div>
          </ModalPopup>
        </div>
        {this.state.customer ? <div className="text-size-12 color-gray2">{this.state.customer.name || this.state.customer.mobile}</div> : null}
      </div>
    );
  }
}

export default CustomerPicker;
